import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import { useTranslation } from "@/context/TranslationContext";

// Geodesic sphere for CTA background
const GeodesicBg = () => (
  <svg
    viewBox="0 0 400 400"
    fill="none"
    className="absolute inset-0 w-full h-full text-electric-blue opacity-[0.06] pointer-events-none"
    preserveAspectRatio="xMidYMid slice"
  >
    <circle cx="200" cy="200" r="192" stroke="currentColor" strokeWidth="0.7" />
    <ellipse cx="200" cy="200" rx="192" ry="64" stroke="currentColor" strokeWidth="0.5" />
    <ellipse cx="200" cy="200" rx="192" ry="115" stroke="currentColor" strokeWidth="0.4" />
    <ellipse cx="200" cy="200" rx="64" ry="192" stroke="currentColor" strokeWidth="0.5" />
    <ellipse cx="200" cy="200" rx="115" ry="192" stroke="currentColor" strokeWidth="0.4" />
    <line x1="8" y1="200" x2="392" y2="200" stroke="currentColor" strokeWidth="0.4" />
    <line x1="200" y1="8" x2="200" y2="392" stroke="currentColor" strokeWidth="0.4" />
    <line x1="64" y1="64" x2="336" y2="336" stroke="currentColor" strokeWidth="0.3" />
    <line x1="336" y1="64" x2="64" y2="336" stroke="currentColor" strokeWidth="0.3" />
    <circle cx="200" cy="8" r="3" fill="currentColor" opacity="0.6" />
    <circle cx="200" cy="392" r="3" fill="currentColor" opacity="0.6" />
    <circle cx="8" cy="200" r="3" fill="currentColor" opacity="0.6" />
    <circle cx="392" cy="200" r="3" fill="currentColor" opacity="0.6" />
    <circle cx="64" cy="64" r="2.5" fill="currentColor" opacity="0.5" />
    <circle cx="336" cy="64" r="2.5" fill="currentColor" opacity="0.5" />
    <circle cx="64" cy="336" r="2.5" fill="currentColor" opacity="0.5" />
    <circle cx="336" cy="336" r="2.5" fill="currentColor" opacity="0.5" />
  </svg>
);

export default function CTASection() {
  const { resolvedTheme } = useTheme();
  const { t } = useTranslation();
  const isLight = resolvedTheme === "light";

  return (
    <section
      className="relative py-32 px-4 md:px-12 lg:px-20 overflow-hidden theme-transition"
      style={{
        background: isLight
          ? "linear-gradient(135deg, #F8FAFF 0%, #F0F4FF 55%, #E8EEFF 100%)"
          : "linear-gradient(135deg, #000000 0%, #0A1628 55%, #0D2040 100%)",
      }}
    >
      {/* Geodesic sphere */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
        <div className="w-[600px] h-[600px] relative">
          <GeodesicBg />
        </div>
      </div>

      {/* Blue orb behind text */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[250px] pointer-events-none"
        style={{
          background: "radial-gradient(ellipse, rgba(27,110,243,0.18) 0%, rgba(0,229,255,0.08) 40%, transparent 70%)",
        }}
      />

      {/* Top circuit border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric-blue/30 to-transparent" />

      <div className="relative z-10 max-w-4xl mx-auto text-center">
        {/* H2 with chromatic on "Product" */}
        <h2
          className="font-black text-white leading-tight mb-6"
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "clamp(2.2rem, 5vw, 4rem)",
            color: isLight ? "#0F172A" : "#FFFFFF",
          }}
        >
          {t("home_cta.title")}{" "}
          <span className="text-electric-blue text-chromatic">{t("home_cta.highlight")}</span>
          {t("home_cta.title_suffix")}
        </h2>

        <p
          className="text-silver text-lg leading-relaxed mb-10 max-w-2xl mx-auto"
          style={{ color: isLight ? "#475569" : "var(--color-text-secondary)" }}
        >
          {t("home_cta.description")}
        </p>

        <div className="flex flex-wrap items-center justify-center gap-4 mb-20">
          <Link
            to="/contact"
            className="flex items-center gap-2.5 bg-electric-blue text-deep-black font-bold px-8 py-4 hover:bg-accent-glow hover:shadow-glow-blue transition-all duration-200 whitespace-nowrap cursor-pointer animate-glow-pulse"
            style={{ borderRadius: "8px" }}
          >
            <i className="ri-rocket-line" />
            {t("home_cta.primary_button")}
          </Link>
          <Link
            to="/contact"
            className="flex items-center gap-2.5 border font-semibold px-8 py-4 hover:bg-white/5 transition-all duration-200 whitespace-nowrap cursor-pointer"
            style={{
              borderRadius: "8px",
              borderColor: isLight ? "rgba(0, 87, 200, 0.2)" : "rgba(255,255,255,0.3)",
              color: isLight ? "#0057C8" : "#FFFFFF",
            }}
          >
            {t("home_cta.secondary_button")}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Trust signals */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
          {[
            { icon: "ri-shield-check-line", textKey: "home_cta.trust1" },
            { icon: "ri-time-line", textKey: "home_cta.trust2" },
            { icon: "ri-code-s-slash-line", textKey: "home_cta.trust3" },
            { icon: "ri-customer-service-line", textKey: "home_cta.trust4" },
          ].map((item) => (
            <div
              key={item.textKey}
              className="flex flex-col items-center gap-2 py-4 px-3 rounded-xl transition-all duration-200"
              style={{
                background: isLight ? "rgba(27, 110, 243, 0.04)" : "rgba(0,229,255,0.04)",
                border: `1px solid ${isLight ? "rgba(27, 110, 243, 0.1)" : "rgba(0,229,255,0.10)"}`,
              }}
            >
              <i
                className={`${item.icon} text-xl`}
                style={{ color: isLight ? "#0057C8" : "#00E5FF" }}
              />
              <span
                className="text-xs font-medium text-center"
                style={{ color: isLight ? "#475569" : "var(--color-text-secondary)" }}
              >
                {t(item.textKey)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
